import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { PageHeader, TableShell, Drawer, Field } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { useFilePreview } from "@/context/FilePreviewContext";
import { toast } from "sonner";
import {
  Upload, FileText, CheckCircle2, AlertCircle, Clock3, XCircle, Trash2, Wrench, Download, Archive, Bell,
  FolderOpen, FilePlus2, ClipboardCheck, ClipboardList, FileSpreadsheet, Settings as SettingsIcon, Send,
} from "lucide-react";

// Roster "missing" entries carry raw field keys (e.g. "fleet_number"); read them in plain
// English on the Compliance tile instead of showing the underscore.
const humanizeField = (key) => String(key).replace(/_/g, " ");

export const STATUS_META = {
  valid:    { label: "Valid",    Icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  expiring: { label: "Expiring", Icon: Clock3,       cls: "text-amber-700 bg-amber-50 border-amber-200" },
  expired:  { label: "Expired",  Icon: XCircle,      cls: "text-rose-700 bg-rose-50 border-rose-200" },
  missing:  { label: "Missing",  Icon: AlertCircle,  cls: "text-slate-600 bg-slate-50 border-slate-200" },
};

export default function Compliance() {
  const [overview, setOverview] = useState({ total_docs: 0, rows: [] });
  const [selected, setSelected] = useState(null); // {driver_id, driver_name}
  const [tab, setTab] = useState("docs"); // docs | maintenance
  const [reminders, setReminders] = useState({ notes_due: [], requests_open: [], counts: { notes_due: 0, requests_open: 0 } });

  const load = () => api.get("/compliance/overview").then((r) => setOverview(r.data));
  const loadReminders = () => api.get("/compliance/reminders-due").then((r) => setReminders(r.data));
  useEffect(() => { load(); loadReminders(); }, []);

  const openDriver = (row) => { setSelected(row); setTab("docs"); };

  const complianceCounts = () => {
    const c = { compliant: 0, at_risk: 0, non_compliant: 0 };
    overview.rows.forEach((r) => {
      if (r.compliant) c.compliant++;
      else if (r.counts.expired > 0 || r.counts.missing > 0) c.non_compliant++;
      else c.at_risk++;
    });
    return c;
  };
  const cc = complianceCounts();

  return (
    <div data-testid="compliance-page">
      <PageHeader subtitle="Regulatory" title="Driver Compliance" />

      <RosterReadyPanel />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Drivers" value={overview.rows.length} />
        <StatCard label="Compliant" value={cc.compliant} color="text-emerald-700" />
        <StatCard label="Non-compliant" value={cc.non_compliant} color="text-rose-700" />
        <StatCard label="Doc types tracked" value={overview.total_docs} />
      </div>

      <RemindersDueCard reminders={reminders} />

      <TableShell
        columns={["Driver", "Status", "Valid", "Expiring", "Expired", "Missing", "Progress", ""]}
        empty={overview.rows.length === 0 ? "No drivers yet — add one from the Drivers page first." : null}
      >
        {overview.rows.map((r) => {
          const complete = r.counts.valid + r.counts.expiring;
          const pct = Math.round((complete / overview.total_docs) * 100);
          const bar = r.compliant ? "bg-emerald-500" : r.counts.expired ? "bg-rose-500" : "bg-amber-500";
          return (
            <tr key={r.driver_id} className="tbl-row border-t border-slate-100" data-testid={`compl-row-${r.driver_id}`}>
              <td className="px-4 py-2.5 font-medium">{r.driver_name}<div className="text-slate-500 text-[11px] capitalize">{r.status?.replace("_", " ")}</div></td>
              <td className="px-4 py-2.5">
                <span className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 border ${r.compliant ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-rose-700 bg-rose-50 border-rose-200"}`}>
                  {r.compliant ? "Compliant" : "Action needed"}
                </span>
              </td>
              <td className="px-4 py-2.5 text-emerald-700 font-mono">{r.counts.valid}</td>
              <td className="px-4 py-2.5 text-amber-700 font-mono">{r.counts.expiring}</td>
              <td className="px-4 py-2.5 text-rose-700 font-mono">{r.counts.expired}</td>
              <td className="px-4 py-2.5 text-slate-600 font-mono">{r.counts.missing}</td>
              <td className="px-4 py-2.5 w-[180px]">
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="text-[11px] text-slate-500 mt-1 font-mono">{complete}/{overview.total_docs} · {pct}%</div>
              </td>
              <td className="px-4 py-2.5">
                <button className="text-[#005E82] hover:underline text-xs mr-3" onClick={() => openDriver(r)} data-testid={`open-driver-${r.driver_id}`}>Open</button>
                <Link to={`/app/drivers/${r.driver_id}/file`} className="text-slate-600 hover:text-[#0A192F] text-xs inline-flex items-center gap-1 mr-3" data-testid={`open-file-${r.driver_id}`}>
                  <FolderOpen size={11} /> Open file
                </Link>
                <button
                  onClick={() => {
                    const token = localStorage.getItem("nemt_token");
                    window.open(`${API_BASE}/drivers/${r.driver_id}/documents/export.zip?auth=${encodeURIComponent(token)}`, "_blank");
                  }}
                  className="text-slate-600 hover:text-[#0A192F] text-xs inline-flex items-center gap-1"
                  data-testid={`zip-${r.driver_id}`}
                >
                  <Archive size={11} /> Zip
                </button>
              </td>
            </tr>
          );
        })}
      </TableShell>

      {selected && (
        <DriverDocsDrawer
          driver={selected}
          tab={tab}
          setTab={setTab}
          onClose={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function RemindersDueCard({ reminders }) {
  const items = [
    ...reminders.notes_due.map((n) => ({ id: `note-${n.id}`, driver_id: n.driver_id, driver_name: n.driver_name, text: n.note, when: `due ${n.remind_on}` })),
    ...reminders.requests_open.map((r) => ({ id: `req-${r.id}`, driver_id: r.driver_id, driver_name: r.driver_name, text: r.title, when: `open since ${String(r.created_at).slice(0, 10)}` })),
  ];
  const total = reminders.counts.notes_due + reminders.counts.requests_open;
  if (total === 0) return null;
  const shown = items.slice(0, 5);

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-5" data-testid="reminders-due-card">
      <div className="flex items-center gap-2 mb-2">
        <Bell size={14} className="text-amber-700" />
        <div className="font-display font-semibold text-amber-900 text-sm">
          Reminders due
          <span className="font-normal text-amber-700 ml-1.5">
            {reminders.counts.notes_due} note{reminders.counts.notes_due === 1 ? "" : "s"}, {reminders.counts.requests_open} open request{reminders.counts.requests_open === 1 ? "" : "s"}
          </span>
        </div>
      </div>
      <div className="space-y-1.5">
        {shown.map((it) => (
          <div key={it.id} className="text-xs text-amber-900 flex flex-wrap items-center justify-between gap-2 bg-white/60 rounded-md px-2.5 py-1.5">
            <span>
              <Link to={`/app/drivers/${it.driver_id}/file`} className="font-medium hover:underline" data-testid={`reminder-link-${it.id}`}>
                {it.driver_name}
              </Link>
              {" · "}
              {it.text}
            </span>
            <span className="text-amber-600 whitespace-nowrap">{it.when}</span>
          </div>
        ))}
      </div>
      {total > 5 && <div className="text-[11px] text-amber-600 mt-2">+{total - 5} more, open a driver's file to see them</div>}
    </div>
  );
}

function StatCard({ label, value, color = "text-[#0A192F]" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</div>
      <div className={`font-display text-xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

// Downloads an authenticated roster export as a blob and saves it with the filename the server
// picked (STS_Drivers_List_<agency>_<date>.xlsx and similar), the same pattern the rest of the
// app uses for any file that needs the auth header rather than a plain URL.
async function downloadRoster(kind, format, includeInactive) {
  const res = await api.get(`/compliance/rosters/${kind}`, {
    params: { format, include: includeInactive ? "all" : "active" },
    responseType: "blob",
  });
  const cd = res.headers["content-disposition"] || "";
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `${kind}_roster.${format}`;
  const blobUrl = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
}

function fmtChanged(iso) {
  if (!iso) return "no records yet";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "no records yet";
  return d.toLocaleDateString();
}

function RosterTile({ kind, title, editPath, data, includeInactive, mndotMissing }) {
  const [busy, setBusy] = useState(null); // "pdf" | "xlsx" | null
  const { openPreview } = useFilePreview();

  // The PDF opens in the same-window drawer first (view before you download); the drawer's own
  // Download button saves it. Excel cannot render in a browser, so that one stays a direct save.
  const viewPdf = () => {
    if (mndotMissing) toast("Add your MnDOT number in Settings so it prints on the roster");
    const include = includeInactive ? "all" : "active";
    openPreview({
      kind: "url",
      id: `/compliance/rosters/${kind}?format=pdf&include=${include}`,
      meta: {
        label: title,
        original_filename: `STS_${kind === "drivers" ? "Drivers" : "Vehicle"}_List.pdf`,
        content_type: "application/pdf",
        canEdit: false,
      },
    });
  };
  const ready = !!data?.ready;
  const missing = data?.missing || [];

  const download = async (format) => {
    setBusy(format);
    try {
      if (mndotMissing) toast("Add your MnDOT number in Settings so it prints on the roster");
      await downloadRoster(kind, format, includeInactive);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 flex-1 min-w-0" data-testid={`roster-tile-${kind}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="font-display font-semibold text-slate-900">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{data?.count ?? 0} record{data?.count === 1 ? "" : "s"}</div>
        </div>
        {ready ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 border text-emerald-700 bg-emerald-50 border-emerald-200" data-testid={`roster-status-${kind}`}>
            <CheckCircle2 size={11} /> Ready to print
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 border text-amber-700 bg-amber-50 border-amber-200" data-testid={`roster-status-${kind}`}>
            <AlertCircle size={11} /> {missing.length} incomplete
          </span>
        )}
      </div>

      {missing.length > 0 && (
        <ul className="mb-3 space-y-1 max-h-32 overflow-y-auto" data-testid={`roster-missing-${kind}`}>
          {missing.slice(0, 8).map((m) => (
            <li key={m.driver_id || m.vehicle_id} className="text-xs">
              <Link to={`${editPath}?edit=${m.driver_id || m.vehicle_id}`} className="text-[#005E82] hover:underline font-medium">
                {m.name || m.label}
              </Link>
              <span className="text-slate-500"> needs {m.fields.map(humanizeField).join(", ")}</span>
            </li>
          ))}
          {missing.length > 8 && <li className="text-[11px] text-slate-400">+{missing.length - 8} more</li>}
        </ul>
      )}

      <div className="text-[11px] text-slate-400 mb-3">Last changed {fmtChanged(data?.last_changed)}</div>

      <div className="flex items-center gap-2">
        <button
          onClick={viewPdf}
          disabled={busy !== null}
          className="text-xs px-3 py-2 rounded-md border border-slate-200 inline-flex items-center gap-1 min-h-[36px] disabled:opacity-60"
          data-testid={`roster-pdf-${kind}`}
        >
          <FileText size={12} /> View PDF
        </button>
        <button
          onClick={() => download("xlsx")}
          disabled={busy !== null}
          className="text-xs px-3 py-2 rounded-md border border-slate-200 inline-flex items-center gap-1 min-h-[36px] disabled:opacity-60"
          data-testid={`roster-xlsx-${kind}`}
        >
          <FileSpreadsheet size={12} /> {busy === "xlsx" ? "Building…" : "Excel"}
        </button>
      </div>
    </div>
  );
}

function RosterReadyPanel() {
  const [status, setStatus] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  const load = () => api.get("/compliance/rosters/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!status) return null;
  const mndotMissing = !status.provider?.mndot_number_set;

  return (
    <div className="mb-5" data-testid="roster-ready-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardCheck size={16} className="text-[#005E82]" />
            <div className="font-display font-semibold text-slate-900">DOT inspection ready</div>
          </div>
          <div className="text-xs text-slate-500 mt-1 max-w-xl">
            MnDOT can ask for these two lists at any inspection. {BRAND} keeps them current from your driver and vehicle
            records, so a new hire or an approved vehicle is on the list the moment you save it.
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 whitespace-nowrap">
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} data-testid="roster-include-inactive" />
          Include inactive
        </label>
      </div>

      {mndotMissing && (
        <div className="mt-3 mb-3 bg-amber-50 border border-amber-200 rounded-md p-3 flex items-center justify-between gap-2 text-xs text-amber-800" data-testid="roster-mndot-banner">
          <span className="flex items-center gap-2"><ClipboardList size={13} /> No MnDOT number on file yet, the rosters will print with that line blank.</span>
          <Link to="/app/settings" className="text-[#005E82] hover:underline inline-flex items-center gap-1 flex-shrink-0">
            <SettingsIcon size={12} /> Add it in Settings
          </Link>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mt-3">
        <RosterTile kind="drivers" title="Driver list" editPath="/app/drivers" data={status.drivers} includeInactive={includeInactive} mndotMissing={mndotMissing} />
        <RosterTile kind="vehicles" title="Vehicle list" editPath="/app/vehicles" data={status.vehicles} includeInactive={includeInactive} mndotMissing={mndotMissing} />
      </div>

      <RegNoticesStrip mndotMissing={mndotMissing} />
    </div>
  );
}

const REG_ACTION_LABEL = { add: "added", delete: "removed", transfer: "transferred" };
const REG_DRIVER_ACTION_LABEL = { add: "hired", delete: "terminated" };

function fmtNoticeDate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// MnDOT requires an STS provider to notify it in writing within ten days of any fleet or driver
// roster change (contracts L and M). This strip surfaces both kinds of notices HichPlan opened
// automatically in one list, counts down the ten days, and lets the operator view the prefilled
// official form (one kind at a time, since a vehicle and a driver print on different forms) or
// mark notices as sent.
function RegNoticesStrip({ mndotMissing }) {
  const { openPreview } = useFilePreview();
  const [notices, setNotices] = useState(null); // { vehicles: {pending, sent}, drivers: {pending, sent} } | null while loading
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(null); // null | "vehicle" | "driver"

  const load = () => api.get("/compliance/reg-notices").then((r) => { setNotices(r.data); setSelected(new Set()); }).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!notices) return null;
  const vehiclePending = notices.vehicles?.pending || [];
  const driverPending = notices.drivers?.pending || [];
  const combined = [
    ...vehiclePending.map((n) => ({ ...n, kind: "vehicle" })),
    ...driverPending.map((n) => ({ ...n, kind: "driver" })),
  ].sort((a, b) => (a.due_on || "").localeCompare(b.due_on || ""));

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const viewForm = (ids, param = "ids", kind = "vehicle") => {
    if (ids.length === 0) return;
    if (mndotMissing) toast("Add your MnDOT number in Settings so it prints on the form");
    const path = kind === "driver" ? "/compliance/reg-notices/driver-form" : "/compliance/reg-notices/form";
    openPreview({
      kind: "url",
      id: `${path}?${param}=${ids.join(",")}`,
      meta: {
        label: kind === "driver" ? "MnDOT driver information form" : "MnDOT vehicle registration form",
        original_filename: kind === "driver" ? "STS_Driver_Information_Form.pdf" : "STS_Vehicle_Registration_Form.pdf",
        content_type: "application/pdf",
        canEdit: false,
      },
    });
  };

  const markSent = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      await Promise.all([...selected].map((id) => api.patch(`/compliance/reg-notices/${id}`, { status: "sent" })));
      toast.success("Marked as sent");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // Selection may mix a vehicle row and a driver row; the two print on different official forms,
  // so "View form" only works when every selected row is the same kind.
  const selectedRows = combined.filter((n) => selected.has(n.id));
  const selectedKinds = new Set(selectedRows.map((n) => n.kind));
  const mixedSelection = selectedKinds.size > 1;
  const viewFormDisabled = selected.size === 0 || mixedSelection;

  const viewSelectedForm = () => {
    if (mixedSelection || selected.size === 0) return;
    const kind = selectedRows[0].kind;
    viewForm([...selected], "ids", kind);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 mt-3" data-testid="reg-notices-strip">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div>
          <div className="font-display font-semibold text-slate-900">MnDOT registration notices</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {`MnDOT requires written notice of any fleet or driver roster change within ten days. ${BRAND} opens the notice the moment you save the record.`}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPickerOpen("vehicle")}
            className="text-xs text-[#005E82] hover:underline"
            data-testid="reg-notice-renewal-link"
          >
            Renewal form
          </button>
          <button
            onClick={() => setPickerOpen("driver")}
            className="text-xs text-[#005E82] hover:underline"
            data-testid="reg-notice-driver-form-link"
          >
            Driver form
          </button>
        </div>
      </div>

      {combined.length === 0 ? (
        <div className="empty text-sm" data-testid="reg-notices-empty">No fleet or driver changes waiting on MnDOT.</div>
      ) : (
        <>
          <div className="space-y-1.5 mb-3" data-testid="reg-notices-list">
            {combined.map((n) => (
              <label key={n.id} className="flex items-center gap-2.5 text-xs bg-slate-50 rounded-md px-2.5 py-2" data-testid={`reg-notice-${n.id}`}>
                <input type="checkbox" checked={selected.has(n.id)} onChange={() => toggle(n.id)} data-testid={`reg-notice-check-${n.id}`} />
                <span className="flex-1">
                  {n.kind === "vehicle" ? (
                    <>
                      {n.fleet_number && <span className="font-mono">#{n.fleet_number} </span>}
                      {n.vehicle_label || "Vehicle"}, {REG_ACTION_LABEL[n.action] || n.action} {fmtNoticeDate(n.event_at)}
                    </>
                  ) : (
                    <>{n.driver_name || "Driver"}, {REG_DRIVER_ACTION_LABEL[n.action] || n.action} {fmtNoticeDate(n.event_at)}</>
                  )}
                </span>
                {n.overdue ? (
                  <span className="text-rose-700 font-medium whitespace-nowrap" data-testid={`reg-notice-overdue-${n.id}`}>
                    overdue by {Math.abs(n.days_left)} day{Math.abs(n.days_left) === 1 ? "" : "s"}
                  </span>
                ) : (
                  <span className="text-slate-500 whitespace-nowrap">notice due in {n.days_left} day{n.days_left === 1 ? "" : "s"}</span>
                )}
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={viewSelectedForm}
              disabled={viewFormDisabled}
              title={mixedSelection ? "Select one kind at a time" : undefined}
              className="text-xs px-3 py-2 rounded-md border border-slate-200 inline-flex items-center gap-1 min-h-[36px] disabled:opacity-60"
              data-testid="reg-notice-view-form"
            >
              <FileText size={12} /> View form
            </button>
            <button
              onClick={markSent}
              disabled={selected.size === 0 || busy}
              className="text-xs px-3 py-2 rounded-md border border-slate-200 inline-flex items-center gap-1 min-h-[36px] disabled:opacity-60"
              data-testid="reg-notice-mark-sent"
            >
              <Send size={12} /> {busy ? "Marking…" : "Mark as sent"}
            </button>
            {mixedSelection && (
              <span className="text-[11px] text-amber-700" data-testid="reg-notice-mixed-hint">
                Select one kind at a time
              </span>
            )}
          </div>
        </>
      )}

      {pickerOpen === "vehicle" && (
        <RegNoticeVehiclePicker
          onClose={() => setPickerOpen(null)}
          onView={(ids) => { setPickerOpen(null); viewForm(ids, "vehicle_ids", "vehicle"); }}
        />
      )}
      {pickerOpen === "driver" && (
        <RegNoticeDriverPicker
          onClose={() => setPickerOpen(null)}
          onView={(ids) => { setPickerOpen(null); viewForm(ids, "driver_ids", "driver"); }}
        />
      )}
    </div>
  );
}

// Simple multi-select of active vehicles, so the operator can view the official form for a
// renewal or a fleet change that never went through the automatic notice (e.g. a plate swap).
function RegNoticeVehiclePicker({ onClose, onView }) {
  const [vehicles, setVehicles] = useState([]);
  const [picked, setPicked] = useState(new Set());

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles((r.data || []).filter((v) => !v.deleted_at && v.status !== "retired")));
  }, []);

  const toggle = (id) => {
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-md p-5" onClick={(e) => e.stopPropagation()} data-testid="reg-notice-picker">
        <div className="font-display font-semibold text-slate-900 mb-1">Renewal form</div>
        <div className="text-xs text-slate-500 mb-3">Pick the vehicles to include on the MnDOT registration form.</div>
        <div className="max-h-72 overflow-y-auto space-y-1 mb-4">
          {vehicles.length === 0 && <div className="text-xs text-slate-400">No active vehicles.</div>}
          {vehicles.map((v) => (
            <label key={v.id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-slate-50" data-testid={`reg-picker-vehicle-${v.id}`}>
              <input type="checkbox" checked={picked.has(v.id)} onChange={() => toggle(v.id)} />
              {v.fleet_number && <span className="font-mono text-xs text-slate-500">#{v.fleet_number}</span>}
              <span>{v.year} {v.make} {v.model}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
          <button
            onClick={() => onView([...picked])}
            disabled={picked.size === 0}
            className="btn-primary disabled:opacity-60"
            data-testid="reg-picker-view"
          >
            View form
          </button>
        </div>
      </div>
    </div>
  );
}

// Simple multi-select of active drivers, so the operator can view the official driver form for
// a periodic full-roster submission that never went through the automatic notice.
function RegNoticeDriverPicker({ onClose, onView }) {
  const [drivers, setDrivers] = useState([]);
  const [picked, setPicked] = useState(new Set());

  useEffect(() => {
    api.get("/drivers").then((r) => setDrivers((r.data || []).filter((d) => !d.deleted_at && d.status !== "suspended" && !d.termination_date)));
  }, []);

  const toggle = (id) => {
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-md p-5" onClick={(e) => e.stopPropagation()} data-testid="reg-notice-driver-picker">
        <div className="font-display font-semibold text-slate-900 mb-1">Driver form</div>
        <div className="text-xs text-slate-500 mb-3">Pick the drivers to include on the MnDOT driver information form.</div>
        <div className="max-h-72 overflow-y-auto space-y-1 mb-4">
          {drivers.length === 0 && <div className="text-xs text-slate-400">No active drivers.</div>}
          {drivers.map((d) => (
            <label key={d.id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-slate-50" data-testid={`reg-driver-picker-${d.id}`}>
              <input type="checkbox" checked={picked.has(d.id)} onChange={() => toggle(d.id)} />
              <span>{d.name}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
          <button
            onClick={() => onView([...picked])}
            disabled={picked.size === 0}
            className="btn-primary disabled:opacity-60"
            data-testid="reg-driver-picker-view"
          >
            View form
          </button>
        </div>
      </div>
    </div>
  );
}

export function DriverDocsDrawer({ driver, tab, setTab, onClose }) {
  const { user } = useAuth();
  const { openPreview } = useFilePreview();
  const canEdit = user?.role === "agency_admin" || user?.role === "dispatcher";
  const isDriver = user?.role === "driver";
  const [checklist, setChecklist] = useState([]);
  const [uploadFor, setUploadFor] = useState(null); // doc_type
  const [maintenance, setMaintenance] = useState([]);
  const [showMaint, setShowMaint] = useState(false);
  const pageInputRefs = React.useRef({});

  const load = () => {
    if (tab === "docs") api.get(`/drivers/${driver.driver_id}/documents`).then((r) => setChecklist(r.data.checklist));
    else if (tab === "maintenance") api.get(`/drivers/${driver.driver_id}/maintenance`).then((r) => setMaintenance(r.data));
  };
  useEffect(() => { load(); }, [tab, driver.driver_id]);

  const viewDocument = (checklistRow, page) => {
    const doc = checklistRow.latest;
    const pageEntry = page ? doc.pages.find((p) => p.n === page) : null;
    openPreview({
      kind: page ? "page" : "document",
      id: doc.id,
      page,
      meta: {
        label: page ? `${checklistRow.label} · page ${page}` : checklistRow.label,
        driver_name: driver.driver_name,
        doc_type: checklistRow.doc_type,
        status: checklistRow.status,
        expires_at: doc.expires_at,
        uploaded_at: page ? pageEntry?.uploaded_at : doc.uploaded_at,
        original_filename: page ? pageEntry?.original_filename : doc.original_filename,
        content_type: page ? pageEntry?.content_type : doc.content_type,
        driver_id: driver.driver_id,
        canEdit: canEdit && !page,
        notes: doc.notes,
        onChanged: load,
      },
    });
  };

  const addPage = async (docId, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/drivers/documents/${docId}/pages`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Page added");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const viewMaintenance = (m) => {
    openPreview({
      kind: "maintenance",
      id: m.id,
      meta: {
        label: m.description || "Maintenance receipt",
        driver_name: driver.driver_name,
        uploaded_at: m.uploaded_at,
        original_filename: m.original_filename,
        content_type: m.content_type,
        driver_id: driver.driver_id,
        canEdit,
        onChanged: load,
      },
    });
  };

  const removeDoc = async (id) => {
    if (!confirm("Delete this document?")) return;
    await api.delete(`/drivers/documents/${id}`);
    toast.success("Deleted"); load();
  };

  const removeMaint = async (id) => {
    if (!confirm("Delete this receipt?")) return;
    await api.delete(`/drivers/maintenance/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <Drawer open={true} onClose={onClose} title={`Compliance · ${driver.driver_name}`}>
      <div className={`grid ${isDriver ? "grid-cols-3" : "grid-cols-2"} gap-1 mb-4 p-1 bg-slate-100 rounded-md`}>
        <button onClick={() => setTab("docs")} className={`text-sm py-1.5 rounded ${tab === "docs" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`} data-testid="tab-docs">Documents</button>
        <button onClick={() => setTab("maintenance")} className={`text-sm py-1.5 rounded ${tab === "maintenance" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`} data-testid="tab-maintenance">Maintenance</button>
        {isDriver && (
          <button onClick={() => setTab("requests")} className={`text-sm py-1.5 rounded ${tab === "requests" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`} data-testid="tab-requests">Requests</button>
        )}
      </div>

      {tab === "docs" && (
        <div className="space-y-2" data-testid="checklist">
          {checklist.map((c) => {
            const meta = STATUS_META[c.status];
            const Icon = meta.Icon;
            return (
              <div key={c.doc_type} className="border border-slate-200 rounded-md p-3" data-testid={`doc-${c.doc_type}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-slate-400" />
                      <div className="font-medium text-sm">{c.label}</div>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-medium rounded-full px-2 py-0.5 border ${meta.cls}`}>
                        <Icon size={10} /> {meta.label}
                      </span>
                    </div>
                    {c.latest && (
                      <div className="text-xs text-slate-500 mt-1">
                        <span className="font-mono">{c.latest.original_filename}</span>
                        {c.latest.expires_at && <span> · expires {c.latest.expires_at}</span>}
                        {c.latest.page_count > 0 && <span> · {c.latest.page_count + 1} pages</span>}
                        <div className="text-slate-400 text-[11px]">Uploaded {new Date(c.latest.uploaded_at).toLocaleDateString()} by {c.latest.uploaded_by_name}</div>
                      </div>
                    )}
                    {c.latest && c.latest.pages.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1">
                        {c.latest.pages.map((p) => (
                          <button key={p.n} onClick={() => viewDocument(c, p.n)} className="text-[11px] text-[#005E82] hover:underline" data-testid={`view-page-${c.doc_type}-${p.n}`}>
                            Page {p.n}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                    {c.latest && (
                      <>
                        <button onClick={() => viewDocument(c)} className="text-[#005E82] hover:underline text-xs" data-testid={`view-${c.doc_type}`}>View</button>
                        <button
                          onClick={() => pageInputRefs.current[c.doc_type]?.click()}
                          className="text-slate-500 hover:text-[#0A192F] text-xs inline-flex items-center gap-1"
                          title="Add another page to this document"
                          data-testid={`addpage-${c.doc_type}`}
                        >
                          <FilePlus2 size={12} /> Add page
                        </button>
                        <input
                          ref={(el) => { pageInputRefs.current[c.doc_type] = el; }}
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) addPage(c.latest.id, f); }}
                        />
                        <button onClick={() => removeDoc(c.latest.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={12} /></button>
                      </>
                    )}
                    <button onClick={() => setUploadFor(c.doc_type)} className="btn-primary text-[11px] px-2 py-1 flex items-center gap-1" data-testid={`upload-${c.doc_type}`}>
                      <Upload size={11} /> {c.latest ? "Replace" : "Upload"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {uploadFor && (
            <UploadDialog
              driverId={driver.driver_id}
              docType={uploadFor}
              label={checklist.find((c) => c.doc_type === uploadFor)?.label}
              onClose={() => setUploadFor(null)}
              onDone={() => { setUploadFor(null); load(); toast.success("Uploaded"); }}
            />
          )}
        </div>
      )}

      {tab === "maintenance" && (
        <div className="space-y-3" data-testid="maintenance-list">
          <button onClick={() => setShowMaint(true)} className="btn-primary text-xs flex items-center gap-1" data-testid="add-maint-btn">
            <Wrench size={12} /> Add receipt
          </button>
          {maintenance.length === 0 && <div className="empty">No maintenance receipts uploaded yet.</div>}
          {maintenance.map((m) => (
            <div key={m.id} className="border border-slate-200 rounded-md p-3" data-testid={`maint-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{m.description || "Maintenance receipt"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    <span className="font-mono">{m.service_date}</span>
                    {m.amount != null && <span> · ${m.amount.toFixed(2)}</span>}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5 truncate">{m.original_filename}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => viewMaintenance(m)} className="text-[#005E82] hover:underline text-xs inline-flex items-center gap-1"><Download size={11} /> View</button>
                  <button onClick={() => removeMaint(m.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}

          {showMaint && (
            <MaintenanceDialog
              driverId={driver.driver_id}
              onClose={() => setShowMaint(false)}
              onDone={() => { setShowMaint(false); load(); toast.success("Uploaded"); }}
            />
          )}
        </div>
      )}

      {tab === "requests" && isDriver && <DriverRequestsPanel driverId={driver.driver_id} />}
    </Drawer>
  );
}

function DriverRequestsPanel({ driverId }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/drivers/${driverId}/requests`).then((r) => setRequests(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [driverId]); // eslint-disable-line react-hooks/exhaustive-deps

  const markDone = async (rid) => {
    try {
      await api.patch(`/drivers/requests/${rid}`, { status: "done" });
      toast.success("Marked done");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <div className="text-xs text-slate-400">Loading…</div>;

  return (
    <div className="space-y-2" data-testid="driver-requests-list">
      <div className="text-xs text-slate-500 mb-1">Requests from your company</div>
      {requests.length === 0 && <div className="empty">Nothing here right now.</div>}
      {requests.map((r) => (
        <div key={r.id} className="border border-slate-200 rounded-md p-3" data-testid={`driver-request-${r.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 border text-[10px] font-medium capitalize ${
                r.status === "open"
                  ? "text-amber-700 bg-amber-50 border-amber-200"
                  : r.status === "done"
                    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                    : "text-slate-500 bg-slate-50 border-slate-200"
              }`}
            >
              {r.status}
            </span>
            <span className="text-[11px] text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
          </div>
          <div className="mt-1 font-medium text-sm text-slate-900">{r.title}</div>
          {r.note && <div className="text-xs text-slate-500 mt-0.5">{r.note}</div>}
          {r.status === "open" && (
            <button
              onClick={() => markDone(r.id)}
              className="btn-primary text-xs px-3 py-2 mt-2 min-h-[40px]"
              data-testid={`driver-request-done-${r.id}`}
            >
              Mark as done
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function UploadDialog({ driverId, docType, label, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      if (expiresAt) fd.append("expires_at", expiresAt);
      if (notes) fd.append("notes", notes);
      fd.append("file", file);
      await api.post(`/drivers/${driverId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="font-display font-semibold text-slate-900 mb-2">Upload · {label}</div>
        <div className="text-xs text-slate-500 mb-4">Accepted: PDF, JPG, PNG, HEIC. Max 20 MB.</div>
        <div className="space-y-3">
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="doc-file" />
          <Field label="Expires on (if applicable)" testId="doc-expires" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-200" placeholder="e.g. Class D, restrictions..." />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
          <button onClick={submit} disabled={busy || !file} className="btn-primary disabled:opacity-60" data-testid="doc-submit">{busy ? "Uploading…" : "Upload"}</button>
        </div>
      </div>
    </div>
  );
}

function MaintenanceDialog({ driverId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [serviceDate, setServiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("service_date", serviceDate);
      if (amount) fd.append("amount", String(amount));
      if (description) fd.append("description", description);
      fd.append("file", file);
      await api.post(`/drivers/${driverId}/maintenance`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="font-display font-semibold text-slate-900 mb-2">Add maintenance receipt</div>
        <div className="space-y-3">
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="maint-file" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Service date" testId="maint-date" type="date" value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} />
            <Field label="Amount ($)" testId="maint-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
              className="mt-1 w-full text-sm px-3 py-2 rounded-md border border-slate-200" placeholder="e.g. Oil change, brake pads..." />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
          <button onClick={submit} disabled={busy || !file} className="btn-primary disabled:opacity-60" data-testid="maint-submit">{busy ? "Uploading…" : "Upload"}</button>
        </div>
      </div>
    </div>
  );
}
